import type { WorldRoomProjection, WorldSessionProjection } from "@cleo/contracts";
import { EllipsisVertical } from "lucide-react";
import type { ReactElement } from "react";

import { cn } from "~/lib/utils";

/**
 * The rail's reveal switches (the principal's decision of 6 Oct 2026):
 * archived rooms and retired sessions stay out of sight by default, and the
 * switch always carries how many there are, so nothing is silently gone.
 */
export function RailRevealToggle({
  name,
  label,
  count,
  on,
  onToggle,
}: {
  /** The switch's `data-room-rail-reveal` hook. */
  readonly name: "archived" | "retired";
  readonly label: string;
  readonly count: number;
  readonly on: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      className="flex h-6 w-full min-w-0 items-center gap-2 rounded-lg px-1 text-left text-[11px] text-muted-foreground outline-hidden transition-colors hover:bg-accent/30 hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
      data-room-rail-reveal={name}
      data-room-rail-reveal-on={on ? "true" : "false"}
      onClick={onToggle}
    >
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex h-3 w-5 shrink-0 items-center rounded-full p-px transition-colors",
          on ? "bg-primary" : "bg-input",
        )}
      >
        <span
          className={cn(
            "block size-2.5 rounded-full bg-background shadow-sm transition-transform motion-reduce:transition-none",
            on && "translate-x-2",
          )}
        />
      </span>
      <span className="min-w-0 flex-1 truncate">
        {label}{" "}
        <span className="font-mono text-[10px]" data-room-rail-reveal-count={name}>
          ({count})
        </span>
      </span>
    </button>
  );
}

type ArchivedRoomMatch = Pick<WorldSessionProjection, "sessionId" | "label">;
const NO_MATCHES: ReadonlyArray<ArchivedRoomMatch> = [];

/**
 * One archived room in the rail's Archived group: it opens by browsing, never
 * by restoring. While the directory is still arriving it waits ("Loading…"):
 * opening it needs the room's newest root, which may not be in yet. A search
 * that found it by a session's name lists those sessions under it, and each
 * opens that session by its id.
 */
export function ArchivedRoomRow({
  room,
  sessionCount,
  matches = NO_MATCHES,
  loading,
  onOpen,
  onOpenSession,
  onMenu,
}: {
  readonly room: WorldRoomProjection;
  readonly sessionCount: number;
  readonly matches?: ReadonlyArray<ArchivedRoomMatch> | undefined;
  /** The directory is still arriving: Open waits for the whole of it. */
  readonly loading: boolean;
  readonly onOpen: () => void;
  readonly onOpenSession: (sessionId: string) => void;
  readonly onMenu: (position: { x: number; y: number }) => void;
}): ReactElement {
  const facts = `${sessionCount} ${sessionCount === 1 ? "session" : "sessions"}${
    room.kind === "workroom" ? " · Workroom" : ""
  }`;
  return (
    <div
      className="flex w-full min-w-0 flex-col rounded-[10px] bg-[color-mix(in_oklab,var(--ncl-text,currentColor)_2.5%,transparent)] shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--muted-foreground,currentColor)_22%,transparent)]"
      data-room-rail-archived-room={room.roomId}
    >
      <div className="group/archived flex h-7 w-full min-w-0 items-center pr-1">
        <button
          type="button"
          aria-disabled={loading ? "true" : undefined}
          aria-label={`${room.label} · archived room · ${facts}${loading ? " · Loading…" : ""}`}
          className={cn(
            "flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded-[10px] px-2 text-left text-xs text-muted-foreground outline-hidden transition-colors focus-visible:ring-1 focus-visible:ring-ring",
            loading ? "cursor-default" : "hover:text-foreground",
          )}
          data-room-rail-archived-loading={loading ? "" : undefined}
          onClick={loading ? undefined : onOpen}
          onContextMenu={(event) => {
            event.preventDefault();
            onMenu({ x: event.clientX, y: event.clientY });
          }}
          title={`${room.label} · archived · ${facts}${loading ? " · Loading…" : ""}`}
        >
          <span className="min-w-0 flex-1 truncate">{room.label}</span>
          {loading ? <span className="shrink-0 text-[10px] opacity-70">Loading…</span> : null}
          <span
            className="font-mono text-[9px] uppercase tracking-[0.08em] opacity-70"
            data-room-rail-record-state="archived-room"
          >
            archived
          </span>
        </button>
        <button
          type="button"
          aria-label={`${room.label} actions`}
          className="flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground opacity-0 outline-hidden hover:bg-accent focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-ring group-hover/archived:opacity-100"
          data-room-rail-menu={`archived:${room.roomId}`}
          onClick={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            onMenu({ x: rect.left, y: rect.bottom + 4 });
          }}
        >
          <EllipsisVertical aria-hidden="true" className="size-3" />
        </button>
      </div>
      {matches.length > 0 ? (
        <div className="flex flex-col gap-0.5 px-1 pb-1.5">
          {matches.map((session) => {
            return (
              <button
                aria-label={`${session.label} · in archived room ${room.label}`}
                className="flex h-6 w-full min-w-0 items-center gap-1.5 rounded-lg py-0 pl-5 pr-1.5 text-left text-[11px] text-muted-foreground outline-hidden transition-colors hover:bg-accent/40 hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
                data-room-rail-archived-session={session.sessionId}
                key={session.sessionId}
                onClick={() => onOpenSession(session.sessionId)}
                title={`${session.label} · open this session`}
                type="button"
              >
                <span
                  aria-hidden="true"
                  className="size-1 shrink-0 rounded-full bg-current opacity-70"
                />
                <span className="min-w-0 flex-1 truncate">{session.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
